import React, {
  createContext,
  ReactElement,
  useCallback,
  useContext,
  useReducer,
} from "react";
import { SnackbarMessage } from "../models";
import { ViError, ViErrorType } from "../models/Errors";
import { Translator } from "../services";
import { useErrorContext } from "./ErrorContext";

interface AppState {
  warnings: SnackbarMessage[];
  error?: SnackbarMessage;
  isDrawerOpen: boolean;
}

export type AppContext = AppState & {
  showError?: (error: SnackbarMessage) => void;
  showWarning?: (warning: SnackbarMessage) => void;
  closeError?: () => void;
  closeWarnings?: () => void;
  toggleDrawer?: (newState?: boolean) => void;
  handleError?: (error: ViError) => void;
};

enum ActionType {
  ShowError = "SHOW ERROR",
  ShowWarning = "SHOW WARNING",
  CloseError = "CLOSE ERROR",
  CloseWarnings = "CLOSE WARNINGS",
  CloseAllNotifications = "CLOSE ALL NOTIFICATIONS",
  ToggleDrawer = "TOGGLE DRAWER",
}

type Action =
  | { type: ActionType.CloseError }
  | { type: ActionType.CloseWarnings }
  | { type: ActionType.ShowError; payload: { error: SnackbarMessage } }
  | { type: ActionType.ShowWarning; payload: { warning: SnackbarMessage } }
  | { type: ActionType.ToggleDrawer; payload?: boolean };

const initialState: AppContext = {
  warnings: [],
  error: undefined,
  isDrawerOpen: false,
};

const AppContext = createContext(initialState);
export const useApp = () => useContext(AppContext);

export const AppProvider = ({ children }: { children: ReactElement }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { report } = useErrorContext();

  const showError = useCallback((error: SnackbarMessage) => {
    dispatch({ type: ActionType.ShowError, payload: { error } });
  }, []);
  const showWarning = useCallback(
    (warning: SnackbarMessage) =>
      dispatch({ type: ActionType.ShowWarning, payload: { warning } }),
    []
  );
  const handleError = useCallback(
    (error: ViError) => {
      if (error.type !== ViErrorType.Silent) {
        const type = `${error.type?.toLowerCase()}s`;
        const { code, name } = error;
        const title = Translator.translate(
          [
            `${type}.${name}.${code}.title`,
            `${type}.${name}.title`,
            `${type}.default.title`,
          ],
          { returnNull: true }
        );
        const message = Translator.translate([
          `${type}.${name}.${code}.message`,
          `${type}.${code}.message`,
          `${type}.default.message`,
        ]);
        if (error.type === ViErrorType.Error) {
          const supplement =
            error.code && error.message
              ? ` (${error.code ? error.code : ""}${
                  error.message ? `: ${error.message}` : ""
                })`
              : "";
          dispatch({
            type: ActionType.ShowError,
            payload: {
              error: {
                title,
                message: message + supplement,
              },
            },
          });
        }
        if (error.type === ViErrorType.Warning)
          if (title && message)
            dispatch({
              type: ActionType.ShowWarning,
              payload: { warning: { title, message } },
            });
      }

      if (error.report) report(error);
    },
    [report]
  );

  const closeError = useCallback(() => dispatch({ type: ActionType.CloseError }), []);
  const closeWarnings = useCallback(
    () => dispatch({ type: ActionType.CloseWarnings }),
    []
  );

  const toggleDrawer = (newState?: boolean) => {
    dispatch({ type: ActionType.ToggleDrawer, payload: newState });
  };

  return (
    <AppContext.Provider
      value={{
        ...state,
        showWarning,
        showError,
        closeError,
        closeWarnings,
        toggleDrawer,
        handleError,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

const reducer = (state: AppState, action: Action): AppState => {
  const newState = (() => {
    switch (action.type) {
      case ActionType.ShowError:
        return { ...state, error: action.payload.error };

      case ActionType.ShowWarning:
        return { ...state, warnings: [...state.warnings, action.payload.warning] };

      case ActionType.CloseError:
        return { ...state, error: undefined };

      case ActionType.CloseWarnings:
        return { ...state, warnings: [] };

      case ActionType.ToggleDrawer:
        const newState = action.payload;
        return {
          ...state,
          isDrawerOpen: typeof newState === "boolean" ? newState : !state.isDrawerOpen,
        };

      default:
        return state;
    }
  })();
  sideEffects(state, action);
  return newState;
};

const sideEffects = (state: AppState, action: Action) => {};
